import { createCallable } from '../firebaseFunctions';

const checkPreviewCodeCallable = createCallable('checkPreviewCode');
const redeemPreviewCodeCallable = createCallable('redeemPreviewCode');

export const previewCodeService = {
  /**
   * Checks a preview code on the server (`checkPreviewCode`), which trims and
   * upper-cases it and logs the attempt. This used to read `previewCodes`
   * straight from Firestore, so a rejected code left no trace anywhere.
   *
   * `retired` means the code exists but is no longer active — burned by a
   * redemption or rescinded by an admin, deliberately without saying which
   * ("you already used this" is wrong for a rescinded code).
   *
   * `accountKind` is who redeeming the code makes you ('student', or null for an
   * ordinary code) — reported by the check as well as the redemption so a client
   * can branch before the account exists.
   *
   * @param {string} code - The preview code as the user typed it
   * @returns {Promise<{valid: boolean, reusable: boolean, retired: boolean, accountKind: string|null}>}
   */
  async validatePreviewCode(code) {
    const { data } = await checkPreviewCodeCallable({ code, platform: 'web' });
    return data;
  },

  /**
   * Redeems a validated preview code for the signed-in user: records it on
   * their user doc and burns it if it is single-use.
   *
   * Call this for EVERY redemption, reusable ones included — the server
   * decides whether to burn. The old client-side write could never have
   * worked: `previewCodes` is admin-write-only, so it came back
   * permission-denied and the failure was swallowed.
   *
   * @param {string} code - The preview code the user entered
   * @returns {Promise<{valid: boolean, reusable: boolean, burned: boolean, accountKind: string|null}>}
   */
  async redeemPreviewCode(code) {
    const { data } = await redeemPreviewCodeCallable({
      code,
      platform: 'web',
    });
    console.log(
      `Preview code ${code} redeemed (valid: ${data.valid}, burned: ${data.burned})`
    );
    return data;
  },
};
